import React, { useEffect } from 'react';
import { V2Nav, V2Footer, V2Kicker, V2_REVEAL as REVEAL } from './V2Shared';
import { V2_COLORS } from './tokens';
import { motion } from 'framer-motion';
import { BRAND } from '../../lib/brand';
import './v2.css';

const fraunces = () => ({ fontFamily: 'Fraunces', fontStyle: 'italic', fontWeight: 300 });

/**
 * V2 Legal shell — dark editorial layout used for both /terms and /privacy.
 * Consistent typography, ample white-space, mono kickers.
 */
function LegalShell({ kicker, title, italicWord, updated, children }) {
  return (
    <div className="min-h-screen antialiased" style={{ background: V2_COLORS.ink, color: V2_COLORS.paper }}>
      <V2Nav scheme="dark" />
      <section className="pt-40 pb-14 px-6 md:px-12 lg:px-20 max-w-[1100px] mx-auto">
        <V2Kicker num="—">{kicker}</V2Kicker>
        <motion.h1 {...REVEAL} className="v2-display mt-6 max-w-[16ch] leading-[0.9]">
          {title.split(italicWord)[0]}
          <em style={fraunces()}>{italicWord}</em>
          {title.split(italicWord)[1] || ''}
        </motion.h1>
        <motion.p {...REVEAL} transition={{ ...REVEAL.transition, delay: 0.1 }} className="mt-8 v2-mono text-[11px] uppercase tracking-[0.24em] text-[#9C978A]">
          Last updated · {updated}
        </motion.p>
      </section>

      <section className="border-t border-white/[0.08] px-6 md:px-12 lg:px-20 max-w-[1100px] mx-auto py-16 md:py-24">
        <div className="legal-body text-[16px] md:text-[17px] leading-[1.75] text-[#CFCBBD] space-y-6">
          {children}
        </div>

        <div className="mt-16 pt-10 border-t border-white/[0.08] flex flex-wrap items-center justify-between gap-4 v2-mono text-[11px] uppercase tracking-[0.24em] text-[#9C978A]">
          <div>© BrightLabs 2026 · Norwich · London · Pune</div>
          <div className="flex gap-6">
            <a href={`mailto:${BRAND.email}`} className="hover:text-white transition-colors">{BRAND.email}</a>
            <a href={`tel:${BRAND.phoneTel}`} className="hover:text-white transition-colors">{BRAND.phoneDisplay}</a>
          </div>
        </div>
      </section>
      <V2Footer />

      {/* Scoped legal typography */}
      <style>{`
        .legal-body h2 { font-family: 'Fraunces', serif; font-weight: 400; font-style: italic; color: #F5F1E8; font-size: clamp(22px,2.6vw,32px); line-height: 1.15; margin-top: 3rem; margin-bottom: 1rem; letter-spacing: -0.01em; }
        .legal-body h2 .n { display: block; font-family: 'JetBrains Mono', monospace; font-style: normal; font-weight: 500; font-size: 11px; letter-spacing: 0.28em; color: #9C978A; text-transform: uppercase; margin-bottom: 0.5rem; }
        .legal-body strong { color: #F5F1E8; font-weight: 700; }
        .legal-body a { color: #E85D2F; text-decoration: underline; text-underline-offset: 3px; transition: color .2s ease; }
        .legal-body a:hover { color: #F5F1E8; }
        .legal-body ul { list-style: none; padding-left: 0; }
        .legal-body ul li { position: relative; padding-left: 1.25rem; }
        .legal-body ul li::before { content: '—'; position: absolute; left: 0; color: #E85D2F; font-family: 'JetBrains Mono', monospace; }
        .legal-body ul li + li { margin-top: 0.4rem; }
      `}</style>
    </div>
  );
}

/* ---------------- TERMS ---------------- */
export function V2Terms() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Terms of Service · BrightLabs';
  }, []);
  return (
    <LegalShell kicker="Legal · Terms" title="Terms of Service." italicWord="Service" updated="August 2026">
      <h2><span className="n">01</span>Agreement</h2>
      <p>By engaging BrightLabs (&ldquo;we&rdquo;, &ldquo;us&rdquo;, &ldquo;our&rdquo;) for any service, you (&ldquo;the Client&rdquo;) agree to these Terms of Service. They apply to every engagement — from a single Websites &amp; Digital project through to a full Growth Partnership.</p>

      <h2><span className="n">02</span>Our services</h2>
      <p>BrightLabs is a modern digital agency combining creative, technology and growth. We take on a deliberately small roster so every engagement gets senior-level attention. Our disciplines are:</p>
      <ul>
        <li><strong>Build</strong> — websites, digital experiences, eCommerce, CRM, custom software, applications and portals</li>
        <li><strong>Create</strong> — brand identity, creative direction, social content, reels &amp; short-form, campaign and ad creative</li>
        <li><strong>Grow</strong> — paid growth (Meta, Google), SEO, conversion optimisation, landing pages and attribution</li>
        <li><strong>Automate</strong> — CRM automation, lead workflows, email/WhatsApp automation, AI workflows and integrations</li>
      </ul>

      <h2><span className="n">03</span>Pricing, billing &amp; invoicing</h2>
      <p><strong>One-off projects</strong> are scoped after a discovery call and quoted in writing. A deposit is payable in advance, with milestone or on-completion payments as agreed. <strong>Monthly retainers</strong> are billed in advance on the same date each month via Stripe or bank transfer. All prices are in GBP for UK engagements (INR for India engagements) and exclude VAT/GST unless stated.</p>
      <p>Late payment may result in services being paused. We do not store payment card details — Stripe handles all card processing.</p>

      <h2><span className="n">04</span>Timelines</h2>
      <p>Custom websites and Growth Partnership engagements have timelines agreed in the kick-off. The clock pauses whenever we&rsquo;re waiting on content or approvals from you.</p>

      <h2><span className="n">05</span>Minimum terms &amp; cancellation</h2>
      <p>Retained services (Creative &amp; Social, Paid Growth, SEO, Growth Partnership) run on a <strong>6-month minimum</strong>. After the minimum term, they continue rolling month-to-month and may be cancelled with <strong>30 days&rsquo; written notice</strong> by email to <a href={`mailto:${BRAND.email}`}>{BRAND.email}</a>. Setup fees are non-refundable once work has begun.</p>
      <p>Where BrightLabs builds, hosts and manages a website as part of a productised offer, the site remains active while the monthly package is active. After the minimum term, if you cancel, you can either let the BrightLabs-managed website close, or request a quotation to transfer or purchase the website. Any transfer or buyout fee depends on the site, functionality, licences and work required to prepare it for handover.</p>

      <h2><span className="n">06</span>Ownership &amp; intellectual property</h2>
      <p>Once paid in full, you own: the website content we produce for you, your logo and brand assets, any custom AI workflows or copy written specifically for your business, and all leads and data captured by your systems.</p>
      <p>BrightLabs retains rights to: our underlying templates, frameworks, internal tooling and reusable code/components used across multiple clients. We may show your finished work in our portfolio unless you ask us not to.</p>

      <h2><span className="n">07</span>Your responsibilities</h2>
      <ul>
        <li>Provide accurate business information, photos and copy when requested</li>
        <li>Respond to reasonable requests for feedback within 5 working days</li>
        <li>Keep your own login credentials confidential</li>
        <li>Ensure any content you supply is lawful and that you own the rights to it</li>
      </ul>

      <h2><span className="n">08</span>Third-party services</h2>
      <p>Your systems may rely on third-party tools (Google, Meta, Stripe, hosting providers, AI providers, SMS gateways, etc.). We are not liable for outages, pricing changes or policy changes made by those providers. Where they charge fees (e.g. SMS credits, ad spend), those are billed to you separately at cost or via your own accounts.</p>

      <h2><span className="n">09</span>Performance &amp; expectations</h2>
      <p>We build systems designed to generate leads, revenue and time. We don&rsquo;t guarantee specific revenue or lead-volume outcomes — those depend on your industry, market, pricing, sales process and follow-through. We do guarantee that what we build is fit for purpose and works as agreed.</p>

      <h2><span className="n">10</span>Limitation of liability</h2>
      <p>Our total liability under these terms is limited to the fees paid by you in the 3 months preceding any claim. We are not liable for indirect or consequential losses (lost profits, lost data, business interruption) caused by third-party outages or factors outside our control.</p>

      <h2><span className="n">11</span>Governing law</h2>
      <p>These terms are governed by the laws of England and Wales. Any disputes will be resolved in the courts of England and Wales.</p>

      <h2><span className="n">12</span>Changes</h2>
      <p>We may update these terms occasionally. Continued use of our services after notification constitutes acceptance.</p>

      <h2><span className="n">13</span>Contact</h2>
      <p>Questions? Email <a href={`mailto:${BRAND.email}`}>{BRAND.email}</a> or call <a href={`tel:${BRAND.phoneTel}`}>{BRAND.phoneDisplay}</a>.</p>
    </LegalShell>
  );
}

/* ---------------- PRIVACY ---------------- */
export function V2Privacy() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Privacy Policy · BrightLabs';
  }, []);
  return (
    <LegalShell kicker="Legal · Privacy" title="Privacy Policy." italicWord="Policy" updated="August 2026 · UK GDPR compliant">
      <h2><span className="n">01</span>Who we are</h2>
      <p>BrightLabs is a modern digital agency combining creative, technology and growth. For data protection purposes, BrightLabs is the data controller for personal data collected through our website and services. Reach us at <a href={`mailto:${BRAND.email}`}>{BRAND.email}</a> or <a href={`tel:${BRAND.phoneTel}`}>{BRAND.phoneDisplay}</a>.</p>

      <h2><span className="n">02</span>What we collect</h2>
      <ul>
        <li><strong>Enquiries:</strong> name, business, email, phone, and any details you share when you send us a brief via Start a Project</li>
        <li><strong>Client account data:</strong> billing details (via Stripe — we never see your card), invoicing info, and the contact details needed to deliver services</li>
        <li><strong>Website analytics:</strong> anonymous traffic data via privacy-friendly tools (page views, referrers, country-level location) — no personal profiles built</li>
        <li><strong>Lead Engine (internal only):</strong> public business information sourced from Google Places (business name, address, website, ratings) — used only internally for outreach research</li>
      </ul>

      <h2><span className="n">03</span>How we use your data</h2>
      <ul>
        <li>Respond to your brief and follow up about our services</li>
        <li>Deliver the engagements you&rsquo;ve signed up for and manage your account</li>
        <li>Send you transactional emails (welcomes, updates, invoices, reminders)</li>
        <li>Improve our website and offering through aggregated analytics</li>
        <li>Comply with legal and accounting obligations</li>
      </ul>
      <p>We will <strong>never</strong> sell your data, never share it with third parties for their own marketing, and never email you unsolicited promotions you didn&rsquo;t opt into.</p>

      <h2><span className="n">04</span>Lawful basis (UK GDPR)</h2>
      <p>We rely on: <strong>contract</strong> (to deliver services you&rsquo;ve requested), <strong>legitimate interest</strong> (to respond to enquiries and run our business), <strong>consent</strong> (for optional marketing — withdraw any time), and <strong>legal obligation</strong> (e.g. accounting records).</p>

      <h2><span className="n">05</span>Tools we use</h2>
      <p>To deliver our services we rely on trusted, GDPR-compliant providers including: <strong>Stripe</strong> (payments), <strong>Zoho Mail</strong> (email), <strong>MongoDB Atlas</strong> (database, EU hosted), <strong>Google Cloud / Vercel</strong> (hosting), and <strong>OpenAI / Anthropic / Google Gemini</strong> (AI features). Data is encrypted in transit and at rest.</p>

      <h2><span className="n">06</span>How long we keep it</h2>
      <p>Enquiry data: up to 24 months unless you become a client (then kept for the duration of our engagement + 6 years for accounting). Marketing data: until you unsubscribe. We delete or anonymise data once it&rsquo;s no longer needed.</p>

      <h2><span className="n">07</span>Your rights</h2>
      <p>Under UK GDPR you have the right to: access, correct, delete, restrict or object to processing of your personal data, request a copy in a portable format, and withdraw consent at any time. Email <a href={`mailto:${BRAND.email}`}>{BRAND.email}</a> and we&rsquo;ll respond within 30 days.</p>
      <p>If you&rsquo;re not happy, you can complain to the Information Commissioner&rsquo;s Office (<a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer">ico.org.uk</a>).</p>

      <h2><span className="n">08</span>Cookies</h2>
      <p>Our website uses only essential cookies (to keep you signed in to any admin area) and lightweight, anonymous analytics. We don&rsquo;t use third-party advertising or tracking cookies.</p>

      <h2><span className="n">09</span>Contact</h2>
      <p>Privacy questions or data requests: <a href={`mailto:${BRAND.email}`}>{BRAND.email}</a> · <a href={`tel:${BRAND.phoneTel}`}>{BRAND.phoneDisplay}</a></p>
    </LegalShell>
  );
}
